import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from './useSuiteStore'
import { withApiState } from '@/utils/withApiState'

export interface UserPreference {
  uuid?: string
  userUuid?: string
  code: PreferenceType
  data: any
  dateCreated?: string
  dateUpdated?: string
}



export enum PreferenceType {
  MAIL = 'MAIL',
  NOTIFICATION = 'NOTIFICATION',
  PAGE = 'PAGE',
  PROSPECTION_BUTTONS = 'PROSPECTION_BUTTONS'
}

export const useUserPreferenceStore = defineStore('userPreference', () => {
  const suiteStore = useSuiteStore()
  const preferences = ref<UserPreference[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)

  const fetchPreferences = async () => {
    await withApiState(
      async () => {
        const response = await suiteStore.suiteFetch('user-preferences/me')
        if (response && response.ok) {
          preferences.value = await response.json()
        } else {
          error.value = 'Failed to fetch preferences'
        }
      },
      { loading, error, errorText: 'An error occurred while fetching preferences' },
    )
  }

  const updatePreference = async (code: PreferenceType, data: any, silent = false) => {
    if (!silent) loading.value = true
    error.value = null
    try {
      const response = await suiteStore.suiteFetch('user-preferences/me', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code, data }),
      })
      if (response && response.ok) {
        const updatedPref = await response.json()
        const index = preferences.value.findIndex((p) => p.code === code)
        if (index !== -1) {
          preferences.value[index] = updatedPref
        } else {
          preferences.value.push(updatedPref)
        }
        return true
      } else {
        error.value = 'Failed to update preference'
        return false
      }
    } catch (err) {
      error.value = 'An error occurred while updating preference'
      console.error(err)
      return false
    } finally {
      if (!silent) loading.value = false
    }
  }

  return {
    preferences,
    loading,
    error,
    fetchPreferences,
    updatePreference,
  }
})
